import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  FileText, BookOpen, ClipboardCheck, Calendar, 
  GraduationCap, Users, Phone, Mail, Clock, CheckCircle2,
  FolderOpen, ArrowRight, ExternalLink, AlertCircle
} from 'lucide-react';

// Documents téléchargeables
const documents = [
  {
    id: 1,
    title: "Livret d'accueil Apprenti",
    description: "Toutes les informations pratiques pour bien démarrer votre formation : règlement intérieur, contacts, organisation, droits et devoirs.",
    icon: BookOpen,
    category: "Accueil",
    type: "page",
    url: "/espace-etudiant/livret-accueil",
    format: "En ligne"
  },
  {
    id: 2,
    title: "Livret de suivi pédagogique",
    description: "Document de suivi mensuel de vos compétences, réunions triadiques avec votre entreprise, gestion des absences et difficultés.",
    icon: ClipboardCheck,
    category: "Suivi",
    type: "form",
    url: "/espace-etudiant/suivi",
    format: "Formulaire"
  },
  {
    id: 3,
    title: "Fiche de positionnement",
    description: "Test de positionnement initial pour évaluer vos compétences commerciales et numériques, et définir votre parcours individualisé.",
    icon: FileText,
    category: "Admission",
    type: "form",
    url: "/espace-etudiant/positionnement",
    format: "Formulaire"
  }
];

// Informations pratiques
const practicalInfo = [
  {
    icon: Calendar,
    title: "Rythme",
    content: "1 jour CFA (lundi) / 4 jours entreprise"
  },
  {
    icon: Clock,
    title: "Horaires CFA",
    content: "9h00 - 12h30 / 13h30 - 17h00"
  },
  {
    icon: Users,
    title: "Effectif",
    content: "20 places maximum par promotion"
  },
  {
    icon: GraduationCap,
    title: "Diplôme",
    content: "TP Niveau 5 (Bac+2) - RNCP39063"
  }
];

// Contacts utiles
const contacts = [
  {
    role: "Responsable pédagogique",
    name: "À définir",
    email: "pedagogie@institut-bia.fr",
    phone: "+33 1 87 66 30 91"
  },
  {
    role: "Référent handicap",
    name: "À définir",
    email: "handicap@institut-bia.fr",
    phone: "+33 1 87 66 30 91"
  },
  {
    role: "Administration",
    name: "Secrétariat",
    email: "contact@institut-bia.fr",
    phone: "+33 1 87 66 30 91"
  }
];

// Étapes clés du parcours
const milestones = [
  { month: "Mois 1-4", title: "BC1 - Prospection", desc: "Veille commerciale, plan d'actions, CRM" },
  { month: "Mois 5", title: "Évaluation CCP1", desc: "MSP blanche et questionnement" },
  { month: "Mois 5-10", title: "BC2 - Négociation", desc: "Propositions commerciales, closing, fidélisation" },
  { month: "Mois 11", title: "Évaluation CCP2", desc: "MSP blanche et questionnement" },
  { month: "Mois 12", title: "Examens finaux", desc: "Jury RNCP - Certification" }
];

export const EspaceEtudiant = () => {
  return (
    <div data-testid="espace-etudiant-page">
      {/* Hero Section */}
      <section className="bg-[#0B1F3A] py-20" data-testid="espace-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
              Espace Étudiant
            </span>
            <h1 className="font-serif-heading text-4xl sm:text-5xl font-semibold text-white mb-6">
              Bienvenue dans votre espace
            </h1>
            <div className="w-16 h-0.5 bg-[#C8A75A] mb-6"></div>
            <p className="text-[#F5F5F5]/90 text-lg leading-relaxed">
              Retrouvez ici tous les documents, ressources et informations utiles 
              pour votre parcours de formation au sein de l'Institut BIA.
            </p>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-6 bg-[#C8A75A]" data-testid="quick-links">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap justify-center gap-4 md:gap-8">
            <a href="#documents" className="text-[#0B1F3A] font-medium hover:underline flex items-center gap-2">
              <FolderOpen size={18} /> Documents
            </a>
            <a href="#planning" className="text-[#0B1F3A] font-medium hover:underline flex items-center gap-2">
              <Calendar size={18} /> Planning
            </a>
            <a href="#parcours" className="text-[#0B1F3A] font-medium hover:underline flex items-center gap-2">
              <GraduationCap size={18} /> Parcours
            </a>
            <a href="#contacts" className="text-[#0B1F3A] font-medium hover:underline flex items-center gap-2">
              <Phone size={18} /> Contacts
            </a>
          </div>
        </div>
      </section>

      {/* Documents Section */}
      <section id="documents" className="py-20 bg-white scroll-mt-20" data-testid="documents-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
              Ressources
            </span>
            <h2 className="font-serif-heading text-3xl font-semibold text-[#0B1F3A]">
              Documents à télécharger
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {documents.map((doc) => (
              <div 
                key={doc.id}
                className="border border-[#E6E6E6] bg-white hover:border-[#C8A75A] transition-all hover:shadow-lg group"
                data-testid={`document-${doc.id}`}
              >
                <div className="bg-[#F5F5F5] p-6 flex items-center justify-between">
                  <doc.icon className="text-[#0B1F3A]" size={32} />
                  <span className="bg-[#0B1F3A] text-[#C8A75A] text-xs px-2 py-1 font-medium">
                    {doc.category}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="font-semibold text-[#0B1F3A] text-lg mb-3">
                    {doc.title}
                  </h3>
                  <p className="text-[#4A4A4A] text-sm leading-relaxed mb-6">
                    {doc.description}
                  </p>
                  <Link 
                    to={doc.url}
                    className="inline-flex items-center gap-2 bg-[#0B1F3A] text-white hover:bg-[#061326] px-4 py-2 transition-colors text-sm font-medium w-full justify-center"
                    data-testid={`open-doc-${doc.id}`}
                  >
                    <ArrowRight size={16} />
                    {doc.type === 'form' ? 'Remplir en ligne' : 'Consulter'}
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* Programme link */}
          <div className="mt-12 text-center">
            <Link to="/programme" className="inline-flex items-center gap-2 text-[#C8A75A] hover:text-[#0B1F3A] font-medium transition-colors">
              <FileText size={18} />
              Consulter le programme détaillé de la formation
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Informations pratiques */}
      <section className="py-20 bg-[#F5F5F5]" data-testid="practical-info">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Info cards */}
            <div>
              <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
                Pratique
              </span>
              <h2 className="font-serif-heading text-3xl font-semibold text-[#0B1F3A] mb-8">
                Informations utiles
              </h2>
              
              <div className="grid grid-cols-2 gap-4">
                {practicalInfo.map((info, index) => (
                  <div key={index} className="bg-white p-6 border border-[#E6E6E6]">
                    <info.icon className="text-[#C8A75A] mb-3" size={24} />
                    <h3 className="font-semibold text-[#0B1F3A] mb-1">{info.title}</h3>
                    <p className="text-[#4A4A4A] text-sm">{info.content}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Important notice */}
            <div>
              <div className="bg-[#0B1F3A] p-8 text-white h-full">
                <AlertCircle className="text-[#C8A75A] mb-4" size={32} />
                <h3 className="font-serif-heading text-xl font-semibold mb-4">
                  Rappels importants
                </h3>
                <ul className="space-y-4 text-[#F5F5F5]/90">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="text-[#C8A75A] flex-shrink-0 mt-0.5" size={18} />
                    <span>Présence obligatoire les lundis au CFA (pointage matin et après-midi)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="text-[#C8A75A] flex-shrink-0 mt-0.5" size={18} />
                    <span>Toute absence doit être justifiée sous 48h (certificat médical ou justificatif)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="text-[#C8A75A] flex-shrink-0 mt-0.5" size={18} />
                    <span>Le livret de suivi doit être rempli chaque mois avec votre maître d'apprentissage</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="text-[#C8A75A] flex-shrink-0 mt-0.5" size={18} />
                    <span>Réunion triadique obligatoire une fois par trimestre</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Planning Link */}
      <section id="planning" className="py-16 bg-white scroll-mt-20" data-testid="planning-link">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Calendar className="mx-auto text-[#C8A75A] mb-4" size={48} />
          <h2 className="font-serif-heading text-3xl font-semibold text-[#0B1F3A] mb-4">
            Consultez le planning
          </h2>
          <p className="text-[#4A4A4A] mb-8">
            Retrouvez le calendrier complet de la formation avec les jours CFA, 
            entreprise, vacances et dates d'examens.
          </p>
          <Link to="/planning">
            <Button className="bg-[#C8A75A] text-[#0B1F3A] hover:bg-[#DDBF78] rounded-none px-8 py-6 text-base font-semibold">
              Voir le planning 2026-2027
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>

      {/* Parcours */}
      <section id="parcours" className="py-20 bg-[#0B1F3A] scroll-mt-20" data-testid="parcours-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
              Votre parcours
            </span>
            <h2 className="font-serif-heading text-3xl font-semibold text-white">
              Les étapes clés de votre formation
            </h2>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-0.5 bg-[#C8A75A]/30 -translate-y-1/2"></div>
            
            <div className="grid md:grid-cols-5 gap-6">
              {milestones.map((milestone, index) => (
                <div key={index} className="relative text-center" data-testid={`milestone-${index}`}>
                  <div className="bg-[#C8A75A] w-4 h-4 rounded-full mx-auto mb-4 relative z-10 hidden md:block"></div>
                  <div className="bg-white/5 backdrop-blur border border-white/10 p-6">
                    <span className="text-[#C8A75A] text-sm font-semibold">{milestone.month}</span>
                    <h3 className="text-white font-semibold mt-2 mb-2">{milestone.title}</h3>
                    <p className="text-[#F5F5F5]/70 text-sm">{milestone.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contacts */}
      <section id="contacts" className="py-20 bg-[#F5F5F5] scroll-mt-20" data-testid="contacts-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
              Besoin d'aide ?
            </span>
            <h2 className="font-serif-heading text-3xl font-semibold text-[#0B1F3A]">
              Contacts utiles
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {contacts.map((contact, index) => (
              <div key={index} className="bg-white p-8 border border-[#E6E6E6] text-center">
                <div className="w-16 h-16 bg-[#0B1F3A] flex items-center justify-center mx-auto mb-4">
                  <Users className="text-[#C8A75A]" size={28} />
                </div>
                <h3 className="font-semibold text-[#0B1F3A] mb-1">{contact.role}</h3>
                <p className="text-[#4A4A4A] text-sm mb-4">{contact.name}</p>
                <div className="space-y-2">
                  <a href={`mailto:${contact.email}`} className="flex items-center justify-center gap-2 text-[#C8A75A] hover:text-[#0B1F3A] text-sm transition-colors">
                    <Mail size={16} /> {contact.email}
                  </a>
                  <div className="flex items-center justify-center gap-2 text-[#4A4A4A] text-sm">
                    <Phone size={16} /> {contact.phone}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA - Not enrolled yet */}
      <section className="py-16 bg-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif-heading text-2xl font-semibold text-[#0B1F3A] mb-4">
            Pas encore inscrit ?
          </h2>
          <p className="text-[#4A4A4A] mb-6">
            Découvrez notre formation et déposez votre candidature en ligne.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/formation">
              <Button variant="outline" className="border-[#0B1F3A] text-[#0B1F3A] hover:bg-[#0B1F3A] hover:text-white rounded-none px-6 py-4">
                Découvrir la formation
              </Button>
            </Link>
            <Link to="/etudiants#candidature">
              <Button className="bg-[#C8A75A] text-[#0B1F3A] hover:bg-[#DDBF78] rounded-none px-6 py-4 font-semibold">
                Déposer ma candidature
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EspaceEtudiant;
